import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_FREEFIELDS,
  RECEIVING_FREEFIELDS,
  RECEIVE_FREEFIELD_METADATA,
  RECEIVE_FREEFIELD_FAILURE,
  SAVING_FREEFIELD,
  SAVING_FREEFIELD_FAILURE,
  UPDATING_FREEFIELD,
  UPDATING_FREEFIELD_FAILURE,
  DELETE_FREEFIELD,
  DELETING_FREEFIELD,
  DELETING_FREEFIELD_FAILURE,
  CLEAR_FREEFIELD_STATE,
  RESET_FREEFIELD_META,
  CLEAR_STATE,
  INCREMENT_FREEFIELD_COUNT
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const freefieldSchema = new schema.Entity('freefields')
const freefieldListSchema = new schema.Array(freefieldSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchFreefieldsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_FREEFIELDS, true)
    return http.get(buildUrlWithQuery('freefields', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_FREEFIELDS, false)
        this.commit(RESET_FREEFIELD_META)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, freefieldListSchema)
          this.commit(RECEIVE_FREEFIELDS, { data: entities.freefields })
          this.commit(RECEIVE_FREEFIELD_METADATA, { next, previous, count, result: result.length })
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVING_FREEFIELDS, false)
        this.commit(RECEIVE_FREEFIELD_FAILURE, handleXHRError(e))
      })
  },
  /**
   * Check a Freefield can be changed
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async checkFreefieldCanChange (store, payload) {
    const { id, ffId } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrlWithQuery(`freefields/${id}/conflicts?column=${ffId}`, null, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (r.hasOwnProperty('count')) {
          return r.count
        }
      })
      .catch(e => {
        console.log(e)
      })
  }
}

const getters = {
  getFreefields: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllFreefields: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getFreefieldMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getFreefieldById: (state) => (id) => {
    return state.cache && state.cache[id] ? state.cache[id] : {}
  },
  getFreefieldFetchStatus: (state) => {
    return state.receivingData
  },
  getFreefieldSaveStatus: (state) => {
    return state.saving
  },
  getFreefieldUpdateStatus: (state) => {
    return state.updating
  },
  getFreefieldDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_FREEFIELDS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_FREEFIELDS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_FREEFIELD_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_FREEFIELD] (state, payload) {
    state.saving = payload
  },
  [SAVING_FREEFIELD_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_FREEFIELD] (state, payload) {
    state.updating = payload
  },
  [UPDATING_FREEFIELD_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_FREEFIELD] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_FREEFIELD] (state, payload) {
    state.deleting = payload
  },
  [DELETING_FREEFIELD_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { id, err }
  },
  [INCREMENT_FREEFIELD_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_FREEFIELD_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_FREEFIELD_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_FREEFIELD_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
